import React, { useState, useEffect, useCallback } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Chip, Box, InputAdornment,
  CircularProgress, TablePagination, MenuItem, Select, Tooltip,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { apiGet, apiPatch, ApiError } from "../api/client";
import { parseImageUrls } from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

function useDebounced(v, ms = 300) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Reason → display metadata. Order here drives the filter-toggle order too.
const REASON_META = {
  low_value: {
    label: "Under $20",
    color: "error",
    explain: "Estimated value below your minimum sellable price. Won't break even on eBay fees + shipping.",
  },
  stale_low_value: {
    label: "Stale low-value",
    color: "warning",
    explain: "Value under your stale-threshold AND the lot has been sitting unsold past the age cutoff. Market won't pay enough.",
  },
  large_low_margin: {
    label: "Large + low margin",
    color: "secondary",
    explain: "Bulky category (pool / furniture / appliance / etc.) with value under threshold. Shipping eats the margin.",
  },
};

const SORT_OPTIONS = [
  { label: "Value (low→high)",  key: "value",         dir: "asc"  },
  { label: "Cost basis (high→low)", key: "cost_basis", dir: "desc" },
  { label: "Age (oldest)",      key: "purchase_date", dir: "asc"  },
  { label: "Title A→Z",         key: "cleaned_title", dir: "asc"  },
];

export default function Trash() {
  const { error: toastError, success: toastSuccess } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ count: 0, sunk_cost: 0, lost_value: 0, by_reason: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 300);
  const [sortIdx, setSortIdx] = useState(0);
  const sortKey = SORT_OPTIONS[sortIdx]?.key || "value";
  const sortDir = SORT_OPTIONS[sortIdx]?.dir || "asc";
  // Reason filter — empty array means show all. Persisted-less for now
  // since the page is intended for periodic "clean house" passes, not
  // an everyday view that needs sticky state.
  const [reasonFilter, setReasonFilter] = useState([]);

  // Thresholds — operator-tunable so they can dial in as they learn the
  // economics of their account. Defaults match the operator-stated
  // "min I can sell is $20" and "large items under ~$40 lose money".
  const [minValue, setMinValue] = useState(20);
  const [staleValue, setStaleValue] = useState(40);
  const [staleDays, setStaleDays] = useState(90);
  const [largeThreshold, setLargeThreshold] = useState(50);
  const debouncedMin    = useDebounced(minValue, 400);
  const debouncedStale  = useDebounced(staleValue, 400);
  const debouncedDays   = useDebounced(staleDays, 400);
  const debouncedLarge  = useDebounced(largeThreshold, 400);

  // Per-row trashing state — keeps the UI responsive when the user mass-
  // trashes a backlog (each row shows its own spinner / vanishes on
  // success rather than blocking the whole grid).
  const [trashing, setTrashing] = useState(() => new Set());

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        sort: sortKey,
        order: sortDir,
        min_value: String(debouncedMin || 0),
        stale_value: String(debouncedStale || 0),
        stale_days: String(debouncedDays || 0),
        large_threshold: String(debouncedLarge || 0),
      });
      if (debounced) params.set("q", debounced);
      if (reasonFilter.length > 0) params.set("reason", reasonFilter.join(","));
      const res = await apiGet(`/api/trash_candidates?${params.toString()}`, { signal });
      setRows(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total) || 0);
      setTotals(res?.totals || { count: 0, sunk_cost: 0, lost_value: 0, by_reason: {} });
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load trash candidates", e instanceof ApiError ? e.message : String(e));
      setRows([]); setTotal(0);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, pageSize, debounced, sortKey, sortDir,
      debouncedMin, debouncedStale, debouncedDays, debouncedLarge,
      reasonFilter, toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  useEffect(() => { setPage(1); }, [
    debounced, sortIdx, pageSize, reasonFilter,
    debouncedMin, debouncedStale, debouncedDays, debouncedLarge,
  ]);

  const trashLot = async (lotId) => {
    setTrashing((s) => new Set(s).add(lotId));
    try {
      await apiPatch("/api/update_location", { lot_id: lotId, location: "Trash" });
      // Remove from local view — next reload won't include it anyway.
      setRows((cur) => cur.filter((r) => r.lot_id !== lotId));
      setTotal((t) => Math.max(0, t - 1));
      toastSuccess("Trashed", lotId);
    } catch (e) {
      toastError("Couldn't trash", e instanceof ApiError ? e.message : String(e));
    } finally {
      setTrashing((s) => {
        const n = new Set(s); n.delete(lotId); return n;
      });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Trash candidates
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {total.toLocaleString()} item{total === 1 ? "" : "s"} worth tossing —
            {" "}{money(totals.sunk_cost)} sunk cost, {money(totals.lost_value)} unrealized
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => load()}
            disabled={loading}
            sx={{ minWidth: { sm: 120 } }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Threshold + search card */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap">
          <TextField
            placeholder="Search title, lot, category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Select
            size="small"
            value={sortIdx}
            onChange={(e) => setSortIdx(Number(e.target.value))}
            sx={{ minWidth: 200 }}
          >
            {SORT_OPTIONS.map((opt, i) => (
              <MenuItem key={opt.key + opt.dir} value={i}>Sort: {opt.label}</MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mt: 1.5 }} flexWrap="wrap">
          <Tooltip title="Items below this estimated value are flagged outright — assumes you can't sell anything for less than this profitably.">
            <TextField
              label="Min value"
              type="number"
              size="small"
              value={minValue}
              onChange={(e) => setMinValue(Number(e.target.value))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ width: { xs: '100%', sm: 130 } }}
            />
          </Tooltip>
          <Tooltip title="Stale-cutoff value: items below this AND older than the age cutoff are flagged. Use this to clear the long tail of borderline-cheap items.">
            <TextField
              label="Stale-value cap"
              type="number"
              size="small"
              value={staleValue}
              onChange={(e) => setStaleValue(Number(e.target.value))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ width: { xs: '100%', sm: 150 } }}
            />
          </Tooltip>
          <TextField
            label="Stale age (days)"
            type="number"
            size="small"
            value={staleDays}
            onChange={(e) => setStaleDays(Number(e.target.value))}
            sx={{ width: { xs: '100%', sm: 130 } }}
          />
          <Tooltip title="Bulky-category items below this value lose money on shipping. Categories matched include Pool, Furniture, Appliance, Treadmill, Grill, etc.">
            <TextField
              label="Large-item cap"
              type="number"
              size="small"
              value={largeThreshold}
              onChange={(e) => setLargeThreshold(Number(e.target.value))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ width: { xs: '100%', sm: 140 } }}
            />
          </Tooltip>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>Reason:</Typography>
          <ToggleButtonGroup
            size="small"
            value={reasonFilter}
            onChange={(_, v) => setReasonFilter(Array.isArray(v) ? v : [])}
          >
            {Object.entries(REASON_META).map(([key, meta]) => {
              const n = totals.by_reason?.[key] || 0;
              return (
                <ToggleButton key={key} value={key}>
                  {meta.label} ({n})
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {/* Card grid */}
      {loading && rows.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress size={14} />
            <span>Loading…</span>
          </Stack>
        </Paper>
      ) : !loading && rows.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6 }}>
          <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Nothing flagged</Typography>
            <Typography variant="caption">
              At the current thresholds, every on-hand lot is worth keeping.
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 1.5,
        }}>
          {rows.map((row) => (
            <TrashCard
              key={row.lot_id}
              row={row}
              onTrash={() => trashLot(row.lot_id)}
              busy={trashing.has(row.lot_id)}
            />
          ))}
        </Box>
      )}

      {/* Pager */}
      <Paper variant="outlined" sx={{ mt: 2 }}>
        <TablePagination
          component="div"
          count={total}
          page={Math.max(0, page - 1)}
          onPageChange={(_, p) => setPage(p + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>
    </Box>
  );
}

function TrashCard({ row, onTrash, busy }) {
  const images = parseImageUrls(row.image_urls);
  const cover = images[0] || null;
  const meta = REASON_META[row.reason] || { label: row.reason, color: "default", explain: "" };
  const title = row.cleaned_title || row.title || "—";

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        opacity: busy ? 0.5 : 1,
        transition: 'opacity 150ms',
      }}
    >
      <Box sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1 / 1',
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {cover ? (
          <img
            src={cover}
            alt={title}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            No image
          </Typography>
        )}
        <Tooltip title={meta.explain} arrow placement="top">
          <Chip
            size="small"
            label={meta.label}
            color={meta.color}
            sx={{
              position: 'absolute', top: 6, left: 6,
              fontWeight: 700, height: 22, fontSize: 11,
            }}
          />
        </Tooltip>
      </Box>

      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500, lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {title}
        </Typography>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Typography
            variant="caption"
            sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.75rem' }}
          >
            {row.lot_id}
          </Typography>
          {row.category_name && (
            <Typography variant="caption" sx={{ color: 'text.disabled', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              · {row.category_name}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" gap={1.5} sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 10 }}>
              VALUE
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
              {money(row.value)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 10 }}>
              COST
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {money(row.cost_basis)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 10 }}>
              AGE
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {row.age_days != null ? `${row.age_days}d` : "—"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ borderTop: 1, borderColor: 'divider', p: 0.75 }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          size="small"
          onClick={onTrash}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
        >
          {busy ? "Trashing…" : "Mark as Trash"}
        </Button>
      </Box>
    </Paper>
  );
}
